import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import styles from "./EditColumns.module.scss";
import { Button } from "@whatslly/ui-components/v2/Button";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import classNames from "classnames";
import type { IEditColumnsStore } from "./types";
import { EditColumnsStoreSymbol } from "./types";
import { useDependencyInjector } from "@whatslly/commons/di";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { MdDragIndicator } from "react-icons/md";
import { FiEdit2, FiCheck, FiX } from "react-icons/fi";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { TFunction } from "i18next";
import { useClickOutside } from "../componentsUtils";

export interface EditColumnsProps {
  className?: string;
  t: TFunction;
}

export const EditColumns: React.FC<EditColumnsProps> = observer(({ className }) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const store = useDependencyInjector<IEditColumnsStore>(EditColumnsStoreSymbol);
  const [editingColumnKey, setEditingColumnKey] = useState<string | null>(null);
  const [editingColumnName, setEditingColumnName] = useState<string>("");

  const resetEditingState = () => {
    setEditingColumnKey(null);
    setEditingColumnName("");
  };

  const dropdownRef = useClickOutside<HTMLDivElement>({
    callback: () => {
      store.close();
      resetEditingState();
    },
    activate: store.isOpen,
    ignoredElements: [buttonRef],
  });

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    store.handleDragEnd(result.source.index, result.destination.index);
  };

  const handleResetToDefault = () => {
    store.resetToDefault();
    store.close();
    resetEditingState();
  };

  const handleEditColumnName = (key: string, currentName: string) => {
    setEditingColumnKey(key);
    setEditingColumnName(currentName);
  };

  const handleSaveColumnName = () => {
    if (editingColumnKey && editingColumnName.trim()) {
      store.updateColumnName(editingColumnKey, editingColumnName);
    }
    resetEditingState();
  };

  const handleCancelEdit = () => {
    resetEditingState();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveColumnName();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  return (
    <div className={styles.editColumnsContainer}>
      <div
        className={
          className ||
          classNames(styles.editColumnsButton, {
            [styles.filterIsOpen]: store.isOpen,
          })
        }
        onClick={() => (store.isOpen ? store.close() : store.open())}
        data-testid="edit-columns-btn"
        ref={buttonRef}
      >
        {t("edit_columns.edit_columns")}
      </div>
      {store.isOpen && (
        <div ref={dropdownRef} className={styles.editColumnsDropdown}>
          <div className={styles.searchBar}>
            <SearchInput
              value={store.getSearch()}
              onChange={(value) => store.setSearch(value)}
              placeholder={t("edit_columns.search_columns")}
              inputClassName={styles.searchInput}
              data-testid="edit-columns-search-input"
            />
          </div>
          <div className={styles.dropdownContent}>
            {!store.getSearch() && (
              <div data-testid="select-all-row" className={classNames(styles.checkboxRow, styles.selectAll)}>
                <CheckBox
                  checked={store.allFilteredSelected() && store.getFilteredColumns().length > 0}
                  text={store.allFilteredSelected() ? t("edit_columns.deselect_all") : t("edit_columns.select_all")}
                  className={styles.selectableCheckboxWrapper}
                  labelClassName={styles.selectableCheckbox}
                  onChange={() => {
                    store.handleSelectAll();
                  }}
                />
              </div>
            )}
            <div className={styles.divider} />
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="columns">
                {(provided) => (
                  <div data-testid="edit-columns-dropdown-content" {...provided.droppableProps} ref={provided.innerRef}>
                    {store.getFilteredColumns().map((col, index) => (
                      <Draggable key={col.key} draggableId={col.key} index={index}>
                        {(provided, snapshot) => (
                          <div
                            data-testid={`checkbox-row`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={classNames(styles.checkboxRow, {
                              [styles.dragging]: snapshot.isDragging,
                            })}
                          >
                            {editingColumnKey !== col.key && (
                              <CheckBox
                                checked={store.getTempSelectedColumns().includes(col.key)}
                                text={col.label}
                                onChange={() => store.toggleColumn(col.key)}
                                className={styles.selectableCheckboxWrapper}
                                labelClassName={styles.selectableCheckbox}
                                data-testid={`selectable-checkbox`}
                              />
                            )}
                            {editingColumnKey === col.key ? (
                              <>
                                <div className={styles.customInputWrapper}>
                                  <TextInput
                                    type="text"
                                    value={editingColumnName}
                                    onChange={(e) => setEditingColumnName(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    autoFocus
                                    className={styles.inlineEditInput}
                                    removeClear={true}
                                    removeBorder={true}
                                    outline="none"
                                    inputSize="small"
                                    containerClassName={styles.inputContainer}
                                  />
                                </div>
                                <div className={styles.editActions}>
                                  <FiCheck
                                    size={18}
                                    className={classNames(styles.confirmEditIcon, {
                                      [styles.disabled]: !editingColumnName.trim(),
                                    })}
                                    onClick={editingColumnName.trim() ? handleSaveColumnName : undefined}
                                    data-testid="confirm-edit"
                                  />
                                  <FiX size={18} className={styles.cancelEditIcon} onClick={handleCancelEdit} data-testid="cancel-edit" />
                                </div>
                              </>
                            ) : (
                              <div
                                className={styles.editNameIcon}
                                onClick={() => handleEditColumnName(col.key, col.label)}
                                data-testid="edit-column-name"
                              >
                                <FiEdit2 size={14} />
                              </div>
                            )}
                            <div {...provided.dragHandleProps} className={styles.dragHandleIcon}>
                              <MdDragIndicator size={20} data-testid="drag-handle-icon" />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {store.getFilteredColumns().length === 0 && <NoSearchResults />}
          </div>
          <div className={styles.dropdownFooter}>
            <div className={styles.footerActions}>
              <span className={styles.selectedCount} data-testid="selected-count">
                {store.getTempSelectedColumns().length} {t("edit_columns.selected")}
              </span>
              <span className={styles.resetLink} onClick={handleResetToDefault} data-testid="reset-columns-btn">
                {t("edit_columns.reset_to_default")}
              </span>
            </div>
            <Button
              data-testid="confirm-select-button"
              type="button"
              onClick={() => store.confirm()}
              disabled={store.getTempSelectedColumns().length === 0}
            >
              {t("edit_columns.select")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

const NoSearchResults = () => {
  const { t } = useTranslation();
  return <div className={styles.noSearchResults}>{t("edit_columns.no_columns_found")}</div>;
};
